import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { ComposerFormValues } from './schema';
import type { ComposeMode } from '../types';
import { RecipientsField } from '~/components/message-composer/components/recipients-badge-input';
import { Button } from '~/components/ui/button';

// Replies have no fields: the server addresses them and sets the subject. A forward is addressed here; only a new email has a subject.
export function EmailFields({
  mode,
  lockTo,
}: {
  mode: ComposeMode;
  lockTo?: boolean;
}) {
  const { t } = useTranslation('mailbox');
  const { control, register, setValue } = useFormContext<ComposerFormValues>();
  const cc = useWatch({ control, name: 'cc' });
  // Cc visibility is the field's own concern, so it stays here rather than
  // being drilled down from the composer. Every default set of Cc recipients is
  // empty on first render, hence the plain `false`.
  const [isCcOpen, setIsCcOpen] = React.useState(false);
  const showCc = isCcOpen || cc.length > 0;

  function toggleCc() {
    if (showCc) {
      setValue('cc', [], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }

    setIsCcOpen(!showCc);
  }

  if (mode !== 'new' && mode !== 'forward') {
    return null;
  }

  return (
    <>
      <div className="flex min-h-8 items-center justify-between gap-2 rounded-md px-3">
        <span className="w-12 shrink-0 text-xs font-normal text-muted-foreground">
          {t('To')}
        </span>
        <div className="flex min-w-0 flex-1 flex-row items-center gap-2">
          <RecipientsField
            name="to"
            readOnly={lockTo === true}
            placeholder="email@example.com"
          />
        </div>
        <Button type="button" variant="muted" size="xs" onClick={toggleCc}>
          {t('Cc')}
        </Button>
      </div>

      {showCc && (
        <div className="flex min-h-8 items-center gap-2 rounded-md px-3">
          <span className="w-12 shrink-0 text-xs font-normal text-muted-foreground">
            {t('Cc')}
          </span>
          <div className="min-w-0 flex-1">
            <RecipientsField
              name="cc"
              readOnly={false}
              placeholder="email@example.com"
            />
          </div>
        </div>
      )}

      {mode === 'new' && (
        <div className="flex min-h-8 items-center gap-2 rounded-md px-3">
          <span className="w-12 shrink-0 text-xs font-normal text-muted-foreground">
            {t('Subject')}
          </span>
          <input
            type="text"
            {...register('subject')}
            className="w-full border-0 bg-transparent text-xs font-normal text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
      )}
    </>
  );
}
